/*
 * @Author: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @Date: 2025-12-19 18:18:51
 * @LastEditors: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @LastEditTime: 2025-12-22 08:58:32
 * @FilePath: \mpc-monorepo\apps\mpc-inner\src\containers\Bidding\ProjectDetail\NewDetail\modal\ScoreDetailDrawer\treeToList.ts
 * @Description: 树形结构与扁平列表之间的双向转换工具函数
 */
import { get, set, cloneDeep, isArray, unset, mergeWith, isObject } from 'lodash';

/**
 * 将树形结构转换为扁平列表
 * 
 * 功能说明：
 * 按照 struct 定义的路径顺序，逐步展开嵌套的数组结构，将树形数据扁平化为列表。
 * 每个展开步骤会将数组元素提取到带 $ 前缀的新字段中，便于表格展示。
 * 
 * 示例：
 * 输入: tree = [{ a: [{ b: 1 }, { b: 2 }] }], struct = ['a']
 * 输出: [{ $a: { b: 1 } }, { $a: { b: 2 } }]
 * 
 * @param tree - 树形结构数组
 * @param struct - 展开路径配置，每个元素可以是字符串或字符串数组
 *                 字符串表示单层路径，数组表示嵌套路径（如 ['a', 'b', 'c'] 表示 a.b.c）
 * @returns 扁平化后的列表数组
 */
export const treeToList = (tree: any[], struct: (string | string[])[]) => {
  // 深拷贝输入数据，避免修改原数据
  let currentList = cloneDeep(tree);
  // 累积的前缀路径，记录之前步骤中已添加的 $ 前缀字段路径
  let prefixPath: string[] = [];

  // 按照 struct 配置的顺序，逐步展开每一层数组
  for (const step of struct) {
    // 将步骤转换为路径数组（支持字符串或数组两种格式）
    const pathKeys = isArray(step) ? step : [step];
    if (pathKeys.length === 0) continue;

    // 解析路径：最后一个元素是目标字段，前面的元素是导航路径
    const targetKey = pathKeys[pathKeys.length - 1]; // 目标字段名
    const navKeys = pathKeys.slice(0, -1); // 导航路径（嵌套对象的路径）

    // 构建访问路径：用于从当前对象中读取要展开的数组
    // 结合之前步骤的前缀路径（已添加 $ 前缀）和当前步骤的导航路径
    const accessPath = [...prefixPath, ...navKeys, targetKey];
    
    // 构建新字段路径：用于存储展开后的单个元素（带 $ 前缀）
    // 例如：如果 targetKey 是 'items'，则新字段名是 '$items'
    const newPrefixPath = [...prefixPath, ...navKeys, `$${targetKey}`];

    // 存储当前步骤展开后的新列表
    const nextList: any[] = [];

    // 遍历当前列表中的每一项
    for (const item of currentList) {
      // 从当前项中获取要展开的数组
      const targetArray = get(item, accessPath);

      if (isArray(targetArray)) {
        if (targetArray.length === 0) {
          // 空数组时跳过，不生成新行（避免产生空数据行）
          continue;
        } else {
          // 遍历数组中的每个元素，为每个元素创建一个新的列表项
          for (const subItem of targetArray) {
            // 深拷贝当前项，避免修改原数据
            const newItem = cloneDeep(item);
            // 将数组元素设置为单个对象/值，放在带 $ 前缀的新字段中
            // 这样就将嵌套的数组结构扁平化了
            set(newItem, newPrefixPath, subItem);
            nextList.push(newItem);
          }
        }
      } else {
        // 如果目标路径不是数组，跳过该项（不符合预期结构）
        continue;
      }
    }
    
    // 更新当前列表为展开后的新列表
    currentList = nextList;
    // 更新前缀路径，用于下一步的访问（下一步会在当前 $ 前缀路径的基础上继续展开）
    prefixPath = newPrefixPath;
  }

  return currentList;
}


/**
 * 将扁平列表转换回树形结构（treeToList 的反向操作）
 * 
 * 功能说明：
 * 按照 struct 定义的路径顺序（反向），逐步将扁平列表还原为树形结构。
 * 将带 $ 前缀的字段重新组织成数组，并通过深度合并的方式保留原有数据。
 * 
 * 合并策略：
 * - 如果目标路径已存在数组，则合并新旧数组（保留原有元素）
 * - 如果目标路径已存在对象，则深度合并对象属性（保留原有属性）
 * - 如果目标路径不存在，则直接设置新值
 * 
 * 示例：
 * 输入: list = [{ $a: { b: 1 } }, { $a: { b: 2 } }], struct = ['a']
 * 输出: [{ a: [{ b: 1 }, { b: 2 }] }]
 * 
 * @param list - 扁平化的列表数组（包含 $ 前缀字段）
 * @param struct - 还原路径配置，必须与 treeToList 使用的 struct 相同
 * @returns 还原后的树形结构数组
 */
export const listToTree = (list: any[], struct: (string | string[])[]) => {
  // 边界情况处理
  if (!isArray(list) || list.length === 0) {
    return [];
  }

  // 深拷贝输入数据，避免修改原数据
  let currentList = cloneDeep(list);
  
  // 反向遍历 struct，从最后一步开始还原
  // 这样可以确保在还原当前步骤时，后续步骤的 $ 前缀字段已经被处理
  for (let i = struct.length - 1; i >= 0; i--) {
    // 获取当前步骤的路径配置
    const step = struct[i];
    const pathKeys = isArray(step) ? step : [step];
    if (pathKeys.length === 0) continue;

    // 解析路径：最后一个元素是目标字段，前面的元素是导航路径
    const targetKey = pathKeys[pathKeys.length - 1]; // 目标字段名
    const navKeys = pathKeys.slice(0, -1); // 导航路径（嵌套对象的路径）

    // ========== 步骤 1: 计算 $ 前缀路径 ==========
    // 计算当前步骤对应的 $ 前缀路径（用于从扁平列表中读取数据）
    // 需要基于之前所有步骤（索引 < i 的步骤）的累积路径（带 $ 前缀）
    // 因为这些步骤在 treeToList 时已经添加了 $ 前缀
    let dollarPrefixPath: string[] = [];
    for (let j = 0; j < i; j++) {
      const prevStep = struct[j];
      const prevPathKeys = isArray(prevStep) ? prevStep : [prevStep];
      if (prevPathKeys.length === 0) continue;
      const prevTargetKey = prevPathKeys[prevPathKeys.length - 1];
      const prevNavKeys = prevPathKeys.slice(0, -1);
      // 累积之前步骤的 $ 前缀路径
      dollarPrefixPath = [...dollarPrefixPath, ...prevNavKeys, `$${prevTargetKey}`];
    }

    // 构建完整的 $ 前缀路径（用于读取当前步骤的扁平数据）
    // 例如：['$score_infos', '$score_group_infos', '$detail']
    const dollarPath = [...dollarPrefixPath, ...navKeys, `$${targetKey}`];
    
    // ========== 步骤 2: 计算原始路径（不带 $ 前缀）==========
    // 计算还原后的原始路径（用于将数据写回树形结构）
    // 在反向处理时，前面的步骤（索引 < i）已经处理过了，所以它们的路径已经不带 $ 前缀了
    let originalPrefixPath: string[] = [];
    for (let j = 0; j < i; j++) {
      const prevStep = struct[j];
      const prevPathKeys = isArray(prevStep) ? prevStep : [prevStep];
      if (prevPathKeys.length === 0) continue;
      const prevTargetKey = prevPathKeys[prevPathKeys.length - 1];
      const prevNavKeys = prevPathKeys.slice(0, -1);
      // 累积之前步骤的原始路径（不带 $ 前缀）
      originalPrefixPath = [...originalPrefixPath, ...prevNavKeys, prevTargetKey];
    }
    // 构建完整的原始路径（用于写入还原后的数据）
    // 例如：['score_infos', 'score_group_infos', 'detail']
    const originalPath = [...originalPrefixPath, ...navKeys, targetKey];

    // ========== 步骤 3: 按照分组键收集 $ 字段的值 ==========
    // 将具有相同分组键的项的 $ 字段值收集到数组中
    // 分组键：除了当前 $ 字段外的所有其他字段的组合
    // 这样可以将原本属于同一个数组的元素重新聚合在一起
    const grouped = new Map<string, any[]>();
    
    for (const item of currentList) {
      // 获取当前项的 $ 字段值（这是要还原到数组中的元素）
      const dollarValue = get(item, dollarPath);
      
      // 如果 $ 字段不存在，跳过该项（可能是其他步骤的数据，暂不处理）
      if (dollarValue === undefined) {
        continue;
      }

      // 创建一个副本用于分组键计算
      const itemForKey = cloneDeep(item);
      // 移除当前 $ 字段，剩余字段作为分组依据
      // 具有相同剩余字段的项应该被聚合到同一个数组中
      unset(itemForKey, dollarPath);
      
      // 生成分组键：将对象序列化为字符串（用于比较对象是否相同）
      // 注意：这种方法要求对象的字段顺序一致，但对于我们的场景已经足够
      const groupKey = JSON.stringify(itemForKey);
      
      // 将当前项的 $ 字段值添加到对应分组的数组中
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey)!.push(dollarValue);
    }

    // ========== 步骤 4: 将分组后的数据合并回树形结构 ==========
    // 遍历列表，将每个分组的数据还原为树形结构
    const nextList: any[] = [];
    const processedKeys = new Set<string>(); // 记录已处理的分组键，避免重复处理
    
    for (const item of currentList) {
      const dollarValue = get(item, dollarPath);
      
      if (dollarValue === undefined) {
        // 如果 $ 字段不存在，说明该项不属于当前步骤的处理范围
        // 直接保留该项（可能是其他步骤的数据，或已经处理过的数据）
        nextList.push(item);
        continue;
      }

      // 计算分组键（与步骤 3 中的逻辑一致）
      const itemForKey = cloneDeep(item);
      unset(itemForKey, dollarPath);
      const groupKey = JSON.stringify(itemForKey);
      
      // 如果这个分组已经处理过，跳过（避免重复处理）
      if (processedKeys.has(groupKey)) {
        continue;
      }
      
      processedKeys.add(groupKey);
      
      // 创建新项：移除 $ 字段，将收集的数组放在原始路径中
      const newItem = cloneDeep(itemForKey);
      // 获取该分组收集到的所有值（这些值原本属于同一个数组）
      const collectedArray = grouped.get(groupKey) || [];
      
      // ========== 步骤 5: 深度合并数据（保留原有结构）==========
      // 检查原始路径是否已存在数据
      const existingValue = get(newItem, originalPath);
      if (existingValue !== undefined) {
        // 如果已存在，进行深度合并（而不是直接替换）
        // 这样可以保留原有数据中不在新数据中的字段和结构
        if (isArray(existingValue) && isArray(collectedArray)) {
          // 情况 1: 都是数组，合并数组（保留原有元素，添加新元素）
          // 注意：这里是将新数组追加到旧数组后面
          // 如果需要去重或其他逻辑，可以在这里扩展
          const mergedArray = [...existingValue, ...collectedArray];
          set(newItem, originalPath, mergedArray);
        } else if (isObject(existingValue) && isObject(collectedArray) && !isArray(existingValue) && !isArray(collectedArray)) {
          // 情况 2: 都是对象（非数组），进行深度合并
          // 使用 mergeWith 自定义合并策略，保留原有属性
          const mergedValue = mergeWith(
            cloneDeep(existingValue), // 原有对象
            collectedArray, // 新对象
            (objValue, srcValue) => {
              // 自定义合并函数
              // 如果都是数组，合并数组
              if (isArray(objValue) && isArray(srcValue)) {
                return [...objValue, ...srcValue];
              }
              // 返回 undefined 表示使用默认的深度合并行为
              return undefined;
            }
          );
          set(newItem, originalPath, mergedValue);
        } else {
          // 情况 3: 类型不匹配或其他情况，使用新值覆盖
          // 这种情况应该不常见，但为了健壮性保留此逻辑
          set(newItem, originalPath, collectedArray);
        }
      } else {
        // 如果原始路径不存在数据，直接设置新值
        set(newItem, originalPath, collectedArray);
      }
      
      nextList.push(newItem);
    }
    
    // 更新当前列表为还原后的新列表
    currentList = nextList;
  }

  return currentList;
}
